import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Grid, Button, Form, Anchor, Spinner } from 'components/base';
import { CloudinaryPicture } from 'components/rich';
import useDataContext from 'data/context/data';

import useUserContext from '../context/user';
import feedbackApi from '../api/feedback';
import usersApi from '../api/users';

const STAGE = {
  PICK: 'pick',
  ELABORATE: 'elaborate',
  DONE: 'done',
};

const linkShape = PropTypes.shape({
  href: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
});

const ratingCopyShape = PropTypes.shape({
  eyebrow: PropTypes.string,
  heading: PropTypes.string.isRequired,
  intro: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  submit: PropTypes.string.isRequired,
  // Optional secondary action that submits the rating with no message body.
  // Makes the textarea's optionality discoverable; without it some users feel
  // they have to type something to send the rating.
  skip: PropTypes.string,
  thanks: PropTypes.string.isRequired,
  doneEyebrow: PropTypes.string,
  doneFollowup: PropTypes.string.isRequired,
  // Optional public-review CTA shown only in the good-done state when
  // `reviewLink` is provided. `{platform}` is substituted from reviewLink.platform.
  review: PropTypes.shape({
    prompt: PropTypes.string.isRequired,
    // Shown instead of `prompt` when the customer submitted a written note —
    // acknowledges the words already exist to lower the perceived effort of
    // repeating them on the review platform.
    promptWithText: PropTypes.string,
    cta: PropTypes.string.isRequired,
  }),
});

// Joins names into a natural English list with an Oxford comma:
//   []                       → ''
//   ['A']                    → 'A'
//   ['A', 'B']               → 'A and B'
//   ['A', 'B', 'C']          → 'A, B, and C'
//   ['A', 'B', 'C', 'D']     → 'A, B, C, and 1 more item'  (cap at 3 names)
const formatList = names => {
  if (names.length === 0) return '';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  if (names.length === 3) return `${names[0]}, ${names[1]}, and ${names[2]}`;

  const head = names.slice(0, 3).join(', ');
  const remaining = names.length - 3;
  return `${head}, and ${remaining} more ${remaining === 1 ? 'item' : 'items'}`;
};

// Resolves an order's items to a deduped list of `{ id, name }` for both copy
// and thumbnail rendering. Prefers the `name` baked into each item at order
// placement; falls back to looking the SKU up in PRODUCT_SPECIFICATION. Drops
// giftnote add-ons (any SKU starting with "giftnote").
const buildProductItems = (order, getSkuProduct) => {
  if (!order || !Array.isArray(order.items)) {
    return [];
  }

  const seen = new Set();
  const items = [];
  order.items.forEach(item => {
    if (!item || !item.id || item.id.startsWith('giftnote')) return;
    const name = item.name || getSkuProduct(item.id)?.name;
    if (!name || seen.has(name)) return;
    seen.add(name);
    items.push({ id: item.id, name });
  });
  return items;
};

// Comma-joined phrase used inline (e.g. "your Photo Book and Calendar").
// Names keep their catalog capitalization so product titles stay legible.
// Falls back when no item resolved.
const formatProductSummary = (items, fallback) => {
  if (items.length === 0) return fallback;
  return formatList(items.map(item => item.name));
};

// Replaces every `{key}` in `template` with `vars[key]`. Missing keys collapse to ''.
const interpolate = (template, vars) => {
  if (!template) {
    return template;
  }
  return template.replace(/\{(\w+)\}/g, (_, key) => (key in vars ? vars[key] : ''));
};

const Eyebrow = ({ children }) => {
  if (!children) {
    return null;
  }
  return (
    <Typography.Span className="small-decorated mb-3">
      <Typography.Span>{children}</Typography.Span>
    </Typography.Span>
  );
};

Eyebrow.propTypes = { children: PropTypes.node };

/**
 * Two-step feedback form for a delivered order.
 *
 * Renders the rating picker, then the empathy + textarea step, then a thank-you state.
 * Looks the order up by `externalId` to personalize copy with the actual product
 * name; copy strings can use `{product}` as a placeholder. Catalog product
 * thumbnails (from Cloudinary, by SKU) appear above the picker. The visual
 * wrapper (Section/Container/tinted background) is the parent's responsibility.
 */
const OrderFeedbackForm = props => {
  const userContext = useUserContext();
  const dataContext = useDataContext();

  // Safe wrapper — `onEvent` is optional, so guard before calling.
  const fireEvent = (action, payload) => {
    if (typeof props.onEvent === 'function') {
      props.onEvent(action, payload);
    }
  };

  const [order, setOrder] = useState(null);
  const [isLoadingOrder, setIsLoadingOrder] = useState(true);
  const [orderLookupFailed, setOrderLookupFailed] = useState(false);

  const [rating, setRating] = useState(null);
  const [text, setText] = useState('');
  // Whether the submitted feedback carried a written note — drives the
  // `review.promptWithText` copy variant in the done state.
  const [submittedWithText, setSubmittedWithText] = useState(false);
  const [stage, setStage] = useState(STAGE.PICK);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fireEvent('feedback_view', { external_id: props.externalId || null });

    if (!props.externalId) {
      fireEvent('feedback_missing_external_id');
    }
    // We intentionally fire view only on mount, regardless of token/externalId
    // changes — those are stable for a given page load.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!props.externalId || !userContext.accessToken) {
      setIsLoadingOrder(false);
      return;
    }

    let cancelled = false;
    (async () => {
      const { data, error: lookupError } = await usersApi(
        userContext.accessToken,
      ).getOrderByExternalId(props.externalId);
      if (cancelled) return;
      if (data) {
        setOrder(data);
        if (data.feedbackLeft) {
          fireEvent('feedback_already_submitted', {
            external_id: props.externalId,
          });
        }
      } else {
        setOrderLookupFailed(true);
        fireEvent('feedback_order_not_found', {
          external_id: props.externalId,
          reason: lookupError || 'unknown',
        });
      }
      setIsLoadingOrder(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [props.externalId, userContext.accessToken]);

  const productItems = useMemo(
    () => buildProductItems(order, dataContext.getSkuProduct),
    [order, dataContext],
  );

  const productSummary = useMemo(
    () => formatProductSummary(productItems, props.fallbackProduct),
    [productItems, props.fallbackProduct],
  );

  const t = useMemo(
    () => (str) =>
      interpolate(str, {
        product: productSummary,
        platform: props.reviewLink?.platform || '',
      }),
    [productSummary, props.reviewLink],
  );

  const sendFeedback = async (chosenRating, overrideText) => {
    if (!props.externalId) {
      setError('We could not find an order reference. Please open the link from your email.');
      return;
    }

    const source = overrideText !== undefined ? overrideText : text;
    const trimmed = source.trim();
    const withText = trimmed.length > 0;

    setIsSubmitting(true);
    setError(null);

    const { error: apiError } = await feedbackApi(userContext.accessToken).submitFeedback(
      props.externalId,
      chosenRating,
      trimmed,
    );

    setIsSubmitting(false);

    if (apiError) {
      fireEvent('feedback_submit_failed', {
        external_id: props.externalId,
        rating: chosenRating,
        with_text: withText,
        error: apiError,
      });
      setError(apiError);
      return;
    }

    fireEvent('feedback_submitted', {
      external_id: props.externalId,
      rating: chosenRating,
      with_text: withText,
    });
    setSubmittedWithText(withText);
    setStage(STAGE.DONE);

    if (chosenRating === 'good' && props.reviewLink && props.copy.good.review) {
      fireEvent('feedback_review_prompt_shown', {
        external_id: props.externalId,
        platform: props.reviewLink.platform,
        with_text: withText,
      });
    }
  };

  const handleReviewClick = () => {
    fireEvent('feedback_review_prompt_clicked', {
      external_id: props.externalId,
      platform: props.reviewLink?.platform,
      with_text: text.trim().length > 0,
    });
  };

  const handlePick = nextRating => {
    fireEvent('feedback_rating_selected', {
      external_id: props.externalId,
      rating: nextRating,
    });
    setRating(nextRating);
    setStage(STAGE.ELABORATE);
    setError(null);
  };

  const handleChangeRating = () => {
    setStage(STAGE.PICK);
    setError(null);
  };

  const handleSubmit = e => {
    e.preventDefault();
    sendFeedback(rating);
  };

  const handleSkip = () => {
    fireEvent('feedback_skip_clicked', {
      external_id: props.externalId,
      rating,
    });
    sendFeedback(rating, '');
  };

  const ratingCopy = rating ? props.copy[rating] : null;

  if (isLoadingOrder) {
    return (
      <div className="text-center py-5">
        <Spinner />
        <Typography.P className="small mt-3">Looking up your order…</Typography.P>
      </div>
    );
  }

  // We couldn't fetch the order (missing/invalid external_id, 404, or 401 because
  // the order belongs to a different user). Show a single error state and don't
  // let feedback be submitted — the backend would reject it for the same reason.
  const cannotLoadOrder = !props.externalId || orderLookupFailed;
  if (cannotLoadOrder && stage !== STAGE.DONE) {
    return (
      <div className="text-center">
        <Typography.H2 className="mb-3">We couldn’t find this order.</Typography.H2>
        <Typography.P className="mb-4 mx-auto mw-600">
          The link may have expired, or this order may belong to a different account. Open the
          feedback link from your delivery email while signed in to the account that placed the
          order.
        </Typography.P>
        {props.supportLink && (
          <Typography.P className="mb-4 mx-auto mw-600">
            Still stuck? <Anchor href={props.supportLink.href}>{props.supportLink.label}</Anchor>
            {' '}and we’ll take it from there.
          </Typography.P>
        )}
        <Anchor href={props.homeLink.href}>{props.homeLink.label}</Anchor>
      </div>
    );
  }

  // Order already has feedback recorded. We don't know whether it was good or
  // bad (and there's no value in re-asking), so we render a neutral thank-you
  // and skip the rating UI entirely. Gated on `stage !== DONE` so a successful
  // submit in this same session still renders the rating-specific thanks
  // (incl. Trustpilot CTA on the good path).
  if (order?.feedbackLeft && stage !== STAGE.DONE && props.copy.alreadyLeft) {
    return (
      <div className="text-center">
        <Eyebrow>{t(props.copy.alreadyLeft.eyebrow)}</Eyebrow>
        <Typography.H2 className="mb-3">{t(props.copy.alreadyLeft.heading)}</Typography.H2>
        <Typography.P className="mb-5 mx-auto mw-600">
          {t(props.copy.alreadyLeft.body)}
        </Typography.P>
        <Anchor href={props.homeLink.href}>{props.homeLink.label}</Anchor>
      </div>
    );
  }

  return (
    <>
      {stage === STAGE.PICK && (
        <div className="text-center">
          {productItems.length > 0 && (
            <div
              className="d-flex justify-content-center align-items-center mb-5"
              style={{ gap: 16, flexWrap: 'wrap' }}
            >
              {productItems.slice(0, 3).map(item => (
                <div
                  key={item.id}
                  style={{
                    background: '#fff',
                    padding: 10,
                    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)',
                    width: productItems.length === 1 ? 180 : 112,
                    flexShrink: 0,
                  }}
                >
                  <CloudinaryPicture
                    imageKey={item.id}
                    type="small"
                    prefix="sku/"
                    alt={item.name}
                    style={{ width: '100%', display: 'block' }}
                  />
                </div>
              ))}
              {productItems.length > 3 && (
                <Typography.Span className="small text-muted">
                  +{productItems.length - 3} more
                </Typography.Span>
              )}
            </div>
          )}
          <Eyebrow>{t(props.copy.pick.eyebrow)}</Eyebrow>
          <Typography.H1 className="mb-3">{t(props.copy.pick.heading)}</Typography.H1>
          <Typography.P className="lead mb-5 mx-auto mw-600">
            {t(props.copy.pick.intro)}
          </Typography.P>

          <Grid.Row className="justify-content-center">
            <Grid.Col xs={12} sm={6} md={5}>
              <Button
                type="primary"
                size="lg"
                block
                disabled={!props.externalId}
                onClick={() => handlePick('good')}
                className="mb-3"
              >
                {t(props.copy.pick.goodLabel)}
              </Button>
            </Grid.Col>
            <Grid.Col xs={12} sm={6} md={5}>
              <Button
                type="outline-primary"
                size="lg"
                block
                disabled={!props.externalId}
                onClick={() => handlePick('bad')}
                className="mb-3"
              >
                {t(props.copy.pick.badLabel)}
              </Button>
            </Grid.Col>
          </Grid.Row>

          {props.copy.pick.footnote && (
            <Typography.P className="small mt-4">{t(props.copy.pick.footnote)}</Typography.P>
          )}
        </div>
      )}

      {stage === STAGE.ELABORATE && ratingCopy && (
        <div>
          <div className="text-center">
            <Typography.P className="small mb-3">
              You said:{' '}
              <strong>
                {t(rating === 'good' ? props.copy.pick.goodLabel : props.copy.pick.badLabel)}
              </strong>{' '}
              · <Anchor onClick={handleChangeRating}>Change</Anchor>
            </Typography.P>
            <Eyebrow>{t(ratingCopy.eyebrow)}</Eyebrow>
            <Typography.H2 className="mb-3">{t(ratingCopy.heading)}</Typography.H2>
            <Typography.P className="mb-4 mx-auto mw-600">{t(ratingCopy.intro)}</Typography.P>
          </div>

          <Form onSubmit={handleSubmit}>
            <Form.Group>
              <Form.Label htmlFor="feedback-text" srOnly>
                Your feedback
              </Form.Label>
              <Form.Input
                id="feedback-text"
                type="textarea"
                name="text"
                rows={5}
                placeholder={t(ratingCopy.placeholder)}
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
            </Form.Group>

            {error && (
              <Typography.P className="text-danger text-center small">{error}</Typography.P>
            )}

            <Grid.Row className="justify-content-center mt-3">
              <Grid.Col xs={12} sm={6}>
                <Button
                  type="primary"
                  size="lg"
                  block
                  htmlType="submit"
                  isLoading={isSubmitting}
                  isLoadingLabel="Sending…"
                >
                  {t(ratingCopy.submit)}
                </Button>
              </Grid.Col>
            </Grid.Row>

            {ratingCopy.skip && (
              <Typography.P className="small text-center mt-3">
                <Anchor onClick={isSubmitting ? undefined : handleSkip}>
                  {t(ratingCopy.skip)}
                </Anchor>
              </Typography.P>
            )}
          </Form>
        </div>
      )}

      {stage === STAGE.DONE && ratingCopy && (
        <div className="text-center">
          <Eyebrow>{t(ratingCopy.doneEyebrow || props.copy.done?.eyebrow)}</Eyebrow>
          <Typography.H2 className="mb-3">{t(ratingCopy.thanks)}</Typography.H2>

          {/* The review ask comes before the followup so the customer sees it
              while still in the moment — the followup reads as a farewell and
              closes the conversation. */}
          {rating === 'good' && props.reviewLink && ratingCopy.review && (
            <div className="mb-5">
              <Typography.P className="mb-4 mx-auto mw-600">
                {t(
                  (submittedWithText && ratingCopy.review.promptWithText) ||
                    ratingCopy.review.prompt,
                )}
              </Typography.P>
              <Grid.Row className="justify-content-center">
                <Grid.Col xs={12} sm={8} md={6}>
                  <Button
                    asLink
                    type="primary"
                    size="lg"
                    block
                    href={props.reviewLink.href}
                    targetBlank
                    onClick={handleReviewClick}
                  >
                    {t(ratingCopy.review.cta)}
                  </Button>
                </Grid.Col>
              </Grid.Row>
            </div>
          )}

          <Typography.P className="mb-5 mx-auto mw-600">{t(ratingCopy.doneFollowup)}</Typography.P>

          <Anchor href={props.homeLink.href}>{props.homeLink.label}</Anchor>
        </div>
      )}
    </>
  );
};

OrderFeedbackForm.propTypes = {
  externalId: PropTypes.string,
  // Substituted into `{product}` placeholders in copy when the order has multiple
  // distinct products or could not be resolved (e.g. "your prints", "your keepsake").
  fallbackProduct: PropTypes.string.isRequired,
  copy: PropTypes.shape({
    pick: PropTypes.shape({
      eyebrow: PropTypes.string,
      heading: PropTypes.string.isRequired,
      intro: PropTypes.string.isRequired,
      goodLabel: PropTypes.string.isRequired,
      badLabel: PropTypes.string.isRequired,
      footnote: PropTypes.string,
    }).isRequired,
    good: ratingCopyShape.isRequired,
    bad: ratingCopyShape.isRequired,
    // Legacy fallback only — prefer `doneEyebrow` per rating so the eyebrow can
    // differ between the good and bad paths.
    done: PropTypes.shape({
      eyebrow: PropTypes.string,
    }),
    // Shown when the order's `feedback_left` flag is true at load time —
    // skips the rating UI altogether. Optional so the component still renders
    // (with the rating UI) for hosts that haven't supplied this copy yet.
    alreadyLeft: PropTypes.shape({
      eyebrow: PropTypes.string,
      heading: PropTypes.string.isRequired,
      body: PropTypes.string.isRequired,
    }),
  }).isRequired,
  homeLink: linkShape.isRequired,
  // Optional support contact shown in the order-not-found state. Recommended
  // as a `mailto:` so users who can't access their order can still reach a human.
  supportLink: linkShape,
  // Optional public-review CTA shown only after a positive submission.
  // Intentionally not offered on the bad path — we don't ask unhappy customers
  // to post publicly. `platform` is substituted into `{platform}` in copy.
  reviewLink: PropTypes.shape({
    href: PropTypes.string.isRequired,
    platform: PropTypes.string.isRequired,
  }),
  // (action, payload) → void. Called for every meaningful step so each app can
  // forward the events to its own analytics pipeline (Gtag + PostHog via tracker).
  // Events fired:
  //   feedback_view                    — page mounted
  //   feedback_missing_external_id     — opened without ?external_id
  //   feedback_order_not_found         — external_id present but order not in first page
  //   feedback_rating_selected         — { rating }
  //   feedback_skip_clicked            — { rating }                  elaborate stage skip link
  //   feedback_submitted               — { rating, with_text }
  //   feedback_submit_failed           — { rating, with_text, error }
  //   feedback_review_prompt_shown     — { platform, with_text }   good-done only
  //   feedback_review_prompt_clicked   — { platform, with_text }   good-done only
  //   feedback_already_submitted       — order loaded with feedback_left = true
  onEvent: PropTypes.func,
};

OrderFeedbackForm.defaultProps = {
  externalId: '',
  onEvent: () => {},
};

export default OrderFeedbackForm;
